import { useEffect } from 'react';
import { selectWorktreeSourceRoot } from '@/lib/desktop-shell/conversation-worktree';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { isLocalMachine } from '@/lib/local-runtime';
import {
  type NativeWorkspaceEnvironment,
  readNativeWorkspaceEnvironment,
} from '@/lib/native-desktop';

/**
 * The environment of `root` written: the shell takes it while `root` is the shown conversation's
 * repository, read when it is written (another conversation may be shown by then).
 */
export function setFolderEnvironment(
  store: DesktopShellStore,
  root: string,
  environment: NativeWorkspaceEnvironment | null,
): void {
  const state = store.getState();
  if (root === (selectWorktreeSourceRoot(state) ?? state.workspace))
    state.setWorkspaceEnvironment(environment);
}

/** Loads the environment attached to the active repository or managed worktree. */
export function useWorkspaceEnvironmentSync({
  store,
  preview,
  workspace,
  worktreeSourceRoot,
}: {
  store: DesktopShellStore;
  preview: boolean;
  workspace: string | null;
  worktreeSourceRoot: string | null;
}): void {
  useEffect(() => {
    const root = worktreeSourceRoot ?? workspace;
    const { setWorkspaceEnvironment } = store.getState();
    if (preview || root === null || !isLocalMachine()) {
      setWorkspaceEnvironment(null);
      return;
    }
    let cancelled = false;
    void readNativeWorkspaceEnvironment(root)
      .then((environment) => {
        if (!cancelled) setWorkspaceEnvironment(environment);
      })
      .catch(() => {
        if (!cancelled) setWorkspaceEnvironment(null);
      });
    return () => {
      cancelled = true;
    };
  }, [preview, store, workspace, worktreeSourceRoot]);
}
